import { useState, useEffect, useCallback } from 'react';
import {
  Lock,
  Unlock,
  Save,
  RotateCcw,
  ChevronDown,
  Loader2,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';
import { financeApi } from '../lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TaxSettings {
  minimumWage: number;
  averageWage: number;
  avgWageSectorQ4: number;
  pitLinearRate: number;
  pitScaleThreshold: number;
  pitScaleRateBelow: number;
  pitScaleRateAbove: number;
  pitScaleTaxFree: number;
  lumpSumRates: Record<string, number>;
  zusEmerytalnaRate: number;
  zusRentowaRate: number;
  zusChorobowaRate: number;
  zusWypadkowaRate: number;
  zusFunduszPracyRate: number;
  zusFullBase: number;
  zusFullTotal: number;
  zusPreferentialBase: number;
  zusPreferentialTotal: number;
  healthScaleRate: number;
  healthLinearRate: number;
  healthMinimum: number;
  healthLumpThreshold1: number;
  healthLumpThreshold2: number;
  healthLumpAmount1: number;
  healthLumpAmount2: number;
  healthLumpAmount3: number;
  healthLinearDeductionLimit: number;
  citSmallRate: number;
  citStandardRate: number;
  citEstonianSmall: number;
  citEstonianStandard: number;
  vatStandard: number;
  vatReduced1: number;
  vatReduced2: number;
  vatZero: number;
  vatExemptionLimit: number;
}

type ValueFormat = 'pln' | 'percent' | 'number';

interface ParamDef {
  key: string;
  label: string;
  format: ValueFormat;
  editable?: boolean;
}

interface SectionDef {
  id: string;
  title: string;
  color: string;      // tailwind gradient from
  colorAccent: string; // icon / badge color
  params: ParamDef[];
}

// ---------------------------------------------------------------------------
// Section definitions
// ---------------------------------------------------------------------------

const SECTIONS: SectionDef[] = [
  {
    id: 'pit-scale',
    title: 'Skala podatkowa PIT',
    color: 'from-blue-600/20 to-blue-800/10',
    colorAccent: 'text-accent-info',
    params: [
      { key: 'pitScaleThreshold', label: 'Próg skali podatkowej', format: 'pln' },
      { key: 'pitScaleTaxFree', label: 'Kwota wolna od podatku', format: 'pln' },
      { key: 'pitScaleRateBelow', label: 'Stawka PIT poniżej progu', format: 'percent' },
      { key: 'pitScaleRateAbove', label: 'Stawka PIT powyżej progu', format: 'percent' },
    ],
  },
  {
    id: 'pit-linear',
    title: 'Podatek liniowy PIT',
    color: 'from-indigo-600/20 to-indigo-800/10',
    colorAccent: 'text-indigo-400',
    params: [
      { key: 'pitLinearRate', label: 'Stawka podatku liniowego', format: 'percent' },
    ],
  },
  {
    id: 'lump-sum',
    title: 'Ryczałt',
    color: 'from-violet-600/20 to-violet-800/10',
    colorAccent: 'text-violet-400',
    params: [], // handled specially — uses lumpSumRates map
  },
  {
    id: 'zus-full',
    title: 'ZUS pełny',
    color: 'from-emerald-600/20 to-emerald-800/10',
    colorAccent: 'text-emerald-400',
    params: [
      { key: 'zusFullBase', label: 'Podstawa wymiaru (60% prognoz. wynagr.)', format: 'pln' },
      { key: 'zusEmerytalnaRate', label: 'Składka emerytalna', format: 'percent' },
      { key: 'zusRentowaRate', label: 'Składka rentowa', format: 'percent' },
      { key: 'zusChorobowaRate', label: 'Składka chorobowa', format: 'percent' },
      { key: 'zusWypadkowaRate', label: 'Składka wypadkowa (zależna od PKD)', format: 'percent', editable: true },
      { key: 'zusFunduszPracyRate', label: 'Fundusz Pracy', format: 'percent' },
      { key: 'zusFullTotal', label: 'Razem ZUS pełny (miesięcznie)', format: 'pln' },
    ],
  },
  {
    id: 'zus-pref',
    title: 'ZUS preferencyjny',
    color: 'from-teal-600/20 to-teal-800/10',
    colorAccent: 'text-teal-400',
    params: [
      { key: 'zusPreferentialBase', label: 'Podstawa wymiaru (30% min. wynagr.)', format: 'pln' },
      { key: 'zusPreferentialTotal', label: 'Razem ZUS preferencyjny (miesięcznie)', format: 'pln' },
    ],
  },
  {
    id: 'health',
    title: 'Składka zdrowotna',
    color: 'from-rose-600/20 to-rose-800/10',
    colorAccent: 'text-rose-400',
    params: [
      { key: 'healthScaleRate', label: 'Stawka zdrowotna (skala)', format: 'percent' },
      { key: 'healthLinearRate', label: 'Stawka zdrowotna (liniowy)', format: 'percent' },
      { key: 'healthMinimum', label: 'Składka minimalna', format: 'pln' },
      { key: 'healthLumpThreshold1', label: 'Próg ryczałtu I', format: 'pln' },
      { key: 'healthLumpThreshold2', label: 'Próg ryczałtu II', format: 'pln' },
      { key: 'healthLumpAmount1', label: 'Kwota zdrowotnej ryczałt I', format: 'pln' },
      { key: 'healthLumpAmount2', label: 'Kwota zdrowotnej ryczałt II', format: 'pln' },
      { key: 'healthLumpAmount3', label: 'Kwota zdrowotnej ryczałt III', format: 'pln' },
      { key: 'healthLinearDeductionLimit', label: 'Limit odliczenia (liniowy)', format: 'pln' },
    ],
  },
  {
    id: 'vat',
    title: 'VAT',
    color: 'from-amber-600/20 to-amber-800/10',
    colorAccent: 'text-amber-400',
    params: [
      { key: 'vatStandard', label: 'Stawka podstawowa', format: 'percent' },
      { key: 'vatReduced1', label: 'Stawka obniżona I', format: 'percent' },
      { key: 'vatReduced2', label: 'Stawka obniżona II', format: 'percent' },
      { key: 'vatZero', label: 'Stawka 0%', format: 'percent' },
      { key: 'vatExemptionLimit', label: 'Limit zwolnienia podmiotowego', format: 'pln' },
    ],
  },
  {
    id: 'base-indicators',
    title: 'Wskaźniki bazowe',
    color: 'from-cyan-600/20 to-cyan-800/10',
    colorAccent: 'text-cyan-400',
    params: [
      { key: 'minimumWage', label: 'Wynagrodzenie minimalne', format: 'pln' },
      { key: 'averageWage', label: 'Przeciętne wynagrodzenie (prognozowane)', format: 'pln' },
      { key: 'avgWageSectorQ4', label: 'Przeciętne w sekt. przedsiębiorstw (IV kw.)', format: 'pln' },
    ],
  },
];

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatValue(value: number | undefined | null, format: ValueFormat): string {
  if (value === undefined || value === null) return '-';
  const num = Number(value);
  switch (format) {
    case 'pln':
      return `${num.toFixed(2)} PLN`;
    case 'percent':
      return `${num.toFixed(2)}%`;
    case 'number':
      return num.toFixed(2);
    default:
      return String(num);
  }
}

// ---------------------------------------------------------------------------
// Collapsible section card
// ---------------------------------------------------------------------------

function SectionCard({
  section,
  effective,
  overrides,
  editableValues,
  onEditableChange,
}: {
  section: SectionDef;
  effective: TaxSettings | null;
  overrides: Record<string, unknown>;
  editableValues: Record<string, string>;
  onEditableChange: (key: string, value: string) => void;
}) {
  const [expanded, setExpanded] = useState(true);

  const isLumpSumSection = section.id === 'lump-sum';
  const lumpSumRates = effective?.lumpSumRates ?? {};

  return (
    <div className="rounded-xl border border-border-subtle bg-surface-raised/50 overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center justify-between px-5 py-4 bg-gradient-to-r ${section.color} hover:brightness-110 transition-all`}
      >
        <h3 className="text-base font-semibold text-text-primary">{section.title}</h3>
        <ChevronDown
          className={`w-5 h-5 text-text-secondary transition-transform duration-200 ${
            expanded ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Body */}
      {expanded && (
        <div className="px-5 py-4 space-y-2">
          {/* Standard params */}
          {section.params.map((param) => {
            const effectiveVal = effective
              ? (effective as unknown as Record<string, number>)[param.key]
              : undefined;
            const hasOverride = param.key in overrides;
            const isEditable = param.editable === true;

            return (
              <div
                key={param.key}
                className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-surface-hover/30 transition-colors group"
              >
                {/* Left: label + badges */}
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {isEditable ? (
                    <Unlock className={`w-4 h-4 flex-shrink-0 ${section.colorAccent}`} />
                  ) : (
                    <Lock className="w-4 h-4 flex-shrink-0 text-text-disabled" />
                  )}
                  <span className="text-sm text-text-secondary truncate">{param.label}</span>
                  {hasOverride && (
                    <span className="flex-shrink-0 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-accent-primary/15 text-accent-primary border border-accent-primary/20">
                      override
                    </span>
                  )}
                </div>

                {/* Right: value */}
                <div className="flex-shrink-0 ml-4">
                  {isEditable ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={editableValues[param.key] ?? ''}
                        onChange={(e) => onEditableChange(param.key, e.target.value)}
                        className="w-28 bg-surface-hover border border-border rounded-lg px-3 py-1.5 text-sm text-text-primary text-right focus:outline-none focus:border-accent-primary/50 focus:ring-1 focus:ring-accent-primary/30 transition-colors"
                      />
                      <span className="text-xs text-text-muted">%</span>
                    </div>
                  ) : (
                    <span
                      className={`text-sm font-mono ${
                        hasOverride ? 'text-accent-primary font-semibold' : 'text-text-primary'
                      }`}
                    >
                      {formatValue(effectiveVal, param.format)}
                    </span>
                  )}
                </div>
              </div>
            );
          })}

          {/* Lump sum rates table */}
          {isLumpSumSection && (
            <div className="space-y-1">
              {Object.entries(lumpSumRates)
                .sort(([a], [b]) => parseFloat(a) - parseFloat(b))
                .map(([rateKey, rateValue]) => (
                  <div
                    key={rateKey}
                    className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-surface-hover/30 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Lock className="w-4 h-4 flex-shrink-0 text-text-disabled" />
                      <span className="text-sm text-text-secondary">
                        Stawka ryczaltu {rateKey}%
                      </span>
                    </div>
                    <span className="text-sm font-mono text-text-primary">
                      {Number(rateValue).toFixed(2)}%
                    </span>
                  </div>
                ))}
              {Object.keys(lumpSumRates).length === 0 && (
                <p className="text-sm text-text-muted py-2 px-3">Brak zdefiniowanych stawek ryczaltu</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function FinanceRulesPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const [effective, setEffective] = useState<TaxSettings | null>(null);
  const [overrides, setOverrides] = useState<Record<string, unknown>>({});
  const [editableValues, setEditableValues] = useState<Record<string, string>>({});

  // ------- Load -------
  const loadRules = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await financeApi.getRules();
      setEffective(data.effective);
      setOverrides(data.overrides ?? {});

      // Initialise editable fields with effective values
      const zusWypadkowaRate = data.effective?.zusWypadkowaRate;
      setEditableValues({
        zusWypadkowaRate: zusWypadkowaRate != null ? String(Number(zusWypadkowaRate)) : '',
      });
    } catch {
      setSaveStatus('error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  // ------- Handlers -------
  const handleEditableChange = (key: string, value: string) => {
    setEditableValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaveStatus('idle');
    setSaving(true);

    try {
      const zusVal = parseFloat(editableValues.zusWypadkowaRate);
      if (isNaN(zusVal) || zusVal < 0 || zusVal > 100) {
        setSaveStatus('error');
        setTimeout(() => setSaveStatus('idle'), 3000);
        setSaving(false);
        return;
      }

      await financeApi.updateRules({ zusWypadkowaRate: zusVal });
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
      await loadRules();
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaveStatus('idle');
    setResetting(true);

    try {
      await financeApi.resetRules();
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
      await loadRules();
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setResetting(false);
    }
  };

  // ------- Render -------
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-text-primary">Reguły podatkowe</h1>
        <p className="text-text-secondary mt-2">
          Aktualne parametry podatkowe i ZUS. Możesz nadpisać stawkę wypadkową odpowiadającą Twojemu kodowi PKD.
        </p>
      </div>

      {/* Sections */}
      <div className="space-y-4">
        {SECTIONS.map((section) => (
          <SectionCard
            key={section.id}
            section={section}
            effective={effective}
            overrides={overrides}
            editableValues={editableValues}
            onEditableChange={handleEditableChange}
          />
        ))}
      </div>

      {/* Sticky save bar */}
      <div className="sticky bottom-0 z-30 -mx-1 px-1 pt-4">
        <div className="flex items-center gap-3 p-4 rounded-xl bg-surface-raised/95 backdrop-blur-sm border border-border shadow-lg">
          <button onClick={handleSave} disabled={saving || resetting} className="btn-primary text-sm flex items-center gap-1.5">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Zapisz nadpisania
          </button>
          {saveStatus === 'success' && (
            <span className="flex items-center gap-1.5 text-xs text-success animate-fade-in">
              <CheckCircle className="w-3.5 h-3.5" /> Zapisano
            </span>
          )}
          {saveStatus === 'error' && (
            <span className="flex items-center gap-1.5 text-xs text-danger animate-fade-in">
              <AlertCircle className="w-3.5 h-3.5" /> Błąd zapisu
            </span>
          )}
          <div className="flex-1" />
          <button
            onClick={handleReset}
            disabled={resetting || saving}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium
                       border border-border text-text-secondary hover:text-text-primary hover:border-border-strong
                       bg-surface-hover/50 hover:bg-surface-muted/50 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {resetting ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
            Resetuj do domyślnych
          </button>
        </div>
      </div>
    </div>
  );
}
